import { type AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { getEndpointWithParams } from 'queries/queries-utils'
import { statusChecksMutationKeys } from 'queries/status-checks/query-keys'
import { STATUS_CHECK_PAYLOAD_MAX_CONTENT_LENGTH } from 'queries/status-checks/query-utils'

import type { StatusCheckCreateRequest } from 'types/api/status-checks/status-check'

type NewStatusCheckId = string

const axiosRequestConfig = {
  maxContentLength: STATUS_CHECK_PAYLOAD_MAX_CONTENT_LENGTH
}

function createStatusCheck([endpoint, params]: ReturnType<
  typeof statusChecksMutationKeys.createStatusCheck
>) {
  return async (payload: StatusCheckCreateRequest) => {
    const endpointWithParams = getEndpointWithParams(endpoint, params)
    const response = await agent.post<NewStatusCheckId>(
      endpointWithParams,
      payload,
      axiosRequestConfig
    )

    return response.data
  }
}

export function useCreateStatusCheck(
  options?: Omit<
    UseMutationOptions<NewStatusCheckId, AxiosError, StatusCheckCreateRequest>,
    'mutationKey' | 'mutationFn'
  >
) {
  const { currentTeamId } = useAuth()
  const mutationKey = statusChecksMutationKeys.createStatusCheck(currentTeamId)

  return useMutation({
    ...options,
    mutationKey,
    mutationFn: createStatusCheck(mutationKey)
  })
}
